/**
 * Het leadkwalificatie-datamodel uit U1. Alle antwoorden zijn gesloten
 * opsommingen, zodat de router zonder vrije tekst een route kan afleiden en
 * een latere CRM-aansluiting geen tekst hoeft te interpreteren.
 *
 * Dit bestand is bewust vrij van React: het is de datalaag, niet de router.
 */

export type Projectroute = "kavel" | "ontwerp" | "bouwrijp";

export type Kavelstatus = "geen" | "op-het-oog" | "in-optie" | "in-bezit";

export type Ontwerpstatus =
  | "idee"
  | "schets"
  | "ontwerp"
  | "vergunning-aangevraagd"
  | "vergunning-onherroepelijk";

export type Projecttype =
  | "vrijstaande-woning"
  | "meerdere-woningen"
  | "verbouw-of-uitbreiding"
  | "utiliteit";

export type Investeringsniveau =
  | "tot-400k"
  | "400-600k"
  | "600-800k"
  | "vanaf-800k"
  | "nog-onbekend";

export type GewensteStart =
  | "zo-snel-mogelijk"
  | "binnen-6-maanden"
  | "binnen-12-maanden"
  | "later"
  | "nog-onbekend";

export type Rol = "particulier" | "architect" | "aannemer" | "ontwikkelaar";

export type Pakketindicatie = "CascoKlaar" | "KlusKlaar" | "WoonKlaar";

export type Leadkwalificatie = {
  // Stap 1 — locatie
  plaats: string;
  kavelstatus: Kavelstatus;

  // Stap 2 tot en met 6
  ontwerpstatus: Ontwerpstatus;
  projecttype: Projecttype;
  investeringsniveau: Investeringsniveau;
  gewenste_start: GewensteStart;
  rol: Rol;

  // Afgeleid, niet gevraagd
  route: Projectroute;
  pakketindicatie: Pakketindicatie | null;

  // Herkomst
  bron_pagina: string;
  bron_component: string;
  gestart_op: string;
};

/** De antwoorden zoals de router ze tijdens het invullen bijhoudt. */
export type Antwoorden = {
  plaats: string;
  kavelstatus?: Kavelstatus;
  ontwerpstatus?: Ontwerpstatus;
  projecttype?: Projecttype;
  investeringsniveau?: Investeringsniveau;
  gewenste_start?: GewensteStart;
  rol?: Rol;
};

/** De sleutels die een stap invult; `plaats` telt niet mee, die is optioneel. */
export type Stapsleutel = Exclude<keyof Antwoorden, "plaats">;

export type Keuze<K extends Stapsleutel> = {
  waarde: NonNullable<Antwoorden[K]>;
  label: string;
  /** Eén regel die de bezoeker helpt herkennen; geen verkoopzin. */
  toelichting: string;
};

export type Stap<K extends Stapsleutel = Stapsleutel> = {
  sleutel: K;
  /** Wat er boven de vraag staat: waar de bezoeker in de reeks zit. */
  eyebrow: string;
  vraag: string;
  /** Waarom we het vragen. De bezoeker mag weten wat er met een antwoord gebeurt. */
  waarom: string;
  keuzes: readonly Keuze<K>[];
};

export const stappen = [
  {
    sleutel: "kavelstatus",
    eyebrow: "Locatie",
    vraag: "Waar komt het huis te staan?",
    waarom: "De plek bepaalt wat mag, wat een kraan kan bereiken en welk deel van je budget al vastligt.",
    keuzes: [
      { waarde: "geen", label: "Nog geen kavel", toelichting: "Je bent aan het zoeken of oriënteren." },
      { waarde: "op-het-oog", label: "Een kavel op het oog", toelichting: "Je hebt een plek gevonden, maar nog niets vastgelegd." },
      { waarde: "in-optie", label: "Kavel in optie", toelichting: "Er ligt een optie of een voorlopige koopovereenkomst." },
      { waarde: "in-bezit", label: "Kavel in bezit", toelichting: "De grond is van jou." },
    ],
  },
  {
    sleutel: "ontwerpstatus",
    eyebrow: "Ontwerp",
    vraag: "Hoe ver is het plan?",
    waarom: "Hier bepalen we of we meedenken over het ontwerp of het klaarmaken voor de fabriek.",
    keuzes: [
      { waarde: "idee", label: "Een idee", toelichting: "Je weet wat je wilt wonen, er ligt nog niets op papier." },
      { waarde: "schets", label: "Een schets", toelichting: "Eerste lijnen van jezelf of van een ontwerper." },
      { waarde: "ontwerp", label: "Een ontwerp", toelichting: "Een uitgewerkt plan, vergunning nog niet aangevraagd." },
      { waarde: "vergunning-aangevraagd", label: "Vergunning aangevraagd", toelichting: "De aanvraag loopt bij de gemeente." },
      { waarde: "vergunning-onherroepelijk", label: "Vergunning onherroepelijk", toelichting: "De bezwaartermijn is voorbij, je mag bouwen." },
    ],
  },
  {
    sleutel: "projecttype",
    eyebrow: "Projecttype",
    vraag: "Wat ga je bouwen?",
    waarom: "Eén woning loopt anders dan een serie. Dat verandert de planning en de rolverdeling.",
    keuzes: [
      { waarde: "vrijstaande-woning", label: "Eén vrijstaande woning", toelichting: "Een huis op een eigen kavel." },
      { waarde: "meerdere-woningen", label: "Meerdere woningen", toelichting: "Twee of meer woningen, herhaald of variërend." },
      { waarde: "verbouw-of-uitbreiding", label: "Verbouw of uitbreiding", toelichting: "Aanbouw, opbouw of vervangende nieuwbouw." },
      { waarde: "utiliteit", label: "Iets anders dan wonen", toelichting: "Bedrijfsruimte, kantoor of een bijzondere functie." },
    ],
  },
  {
    sleutel: "investeringsniveau",
    eyebrow: "Investering",
    vraag: "Waar ligt je budget voor het huis?",
    waarom: "Alleen het huis, dus zonder kavel, leges, kelder en tuin. Zo kunnen we een pakket noemen dat klopt.",
    keuzes: [
      { waarde: "tot-400k", label: "Tot € 400.000", toelichting: "Ruwbouw en schil af, afbouw doe je zelf of met je eigen partij." },
      { waarde: "400-600k", label: "€ 400.000 – € 600.000", toelichting: "Verder afgewerkt, met ruimte voor eigen keuzes." },
      { waarde: "600-800k", label: "€ 600.000 – € 800.000", toelichting: "Instapklaar opgeleverd." },
      { waarde: "vanaf-800k", label: "Vanaf € 800.000", toelichting: "Groter, bijzonderder of met veel maatwerk." },
      { waarde: "nog-onbekend", label: "Dat weet ik nog niet", toelichting: "Dan rekenen we het samen door voordat je iets vastlegt." },
    ],
  },
  {
    sleutel: "gewenste_start",
    eyebrow: "Planning",
    vraag: "Wanneer wil je bouwen?",
    waarom: "Productieplekken worden vooruit gepland. Je datum bepaalt wat er eerst gereed moet zijn.",
    keuzes: [
      { waarde: "zo-snel-mogelijk", label: "Zo snel als het kan", toelichting: "Je wilt de eerste vrije productieplek." },
      { waarde: "binnen-6-maanden", label: "Binnen een half jaar", toelichting: "Er is nog tijd voor de technische uitwerking." },
      { waarde: "binnen-12-maanden", label: "Binnen een jaar", toelichting: "Ontwerp en vergunning kunnen nog rustig hun ronde doen." },
      { waarde: "later", label: "Later dan een jaar", toelichting: "Je bereidt voor en wilt weten waar je rekening mee houdt." },
      { waarde: "nog-onbekend", label: "Dat hangt ergens van af", toelichting: "Bijvoorbeeld van de verkoop van je huis of van de gemeente." },
    ],
  },
  {
    sleutel: "rol",
    eyebrow: "Jouw rol",
    vraag: "Vanuit welke rol kijk je hiernaar?",
    waarom: "De projectfase blijft gelijk. Wie wat doet, verandert wel.",
    keuzes: [
      { waarde: "particulier", label: "Particulier", toelichting: "Je bouwt je eigen huis." },
      { waarde: "architect", label: "Architect", toelichting: "Je houdt de ontwerpregie, wij vertalen naar productie." },
      { waarde: "aannemer", label: "Aannemer", toelichting: "Je bouwt, wij leveren en monteren de prefab onderdelen." },
      { waarde: "ontwikkelaar", label: "Ontwikkelaar", toelichting: "Je stuurt op herhaling, planning en opleverbaarheid." },
    ],
  },
] as const satisfies readonly Stap[];

export const aantalStappen = stappen.length;

/**
 * De route volgt uit stap 1 en 2, in deze volgorde; de eerste regel die klopt,
 * wint. Overgenomen uit de afleidingsregels in U1.
 */
export function leidRouteAf(antwoorden: Pick<Antwoorden, "kavelstatus" | "ontwerpstatus">): Projectroute {
  const { kavelstatus, ontwerpstatus } = antwoorden;
  if (ontwerpstatus === "vergunning-onherroepelijk" && kavelstatus === "in-bezit") return "bouwrijp";
  if (ontwerpstatus === "ontwerp" || ontwerpstatus === "vergunning-aangevraagd") return "ontwerp";
  return "kavel";
}

/**
 * De pakketindicatie volgt uit stap 4 en is nadrukkelijk een indicatie, geen
 * prijs. `nog-onbekend` levert bewust niets op: dan verwijzen we door naar de
 * prijspagina in plaats van een bedrag te suggereren.
 */
export function leidPakketAf(niveau: Investeringsniveau | undefined): Pakketindicatie | null {
  switch (niveau) {
    case "tot-400k": return "CascoKlaar";
    case "400-600k": return "KlusKlaar";
    case "600-800k":
    case "vanaf-800k": return "WoonKlaar";
    default: return null;
  }
}

/** De noot die bij elk genoemd bedrag hoort. Staat zo ook elders op de site. */
export const prijsnoot =
  "Richtprijs per m³ woninginhoud, prijspeil 2026. Geen offerte. Exclusief kavel, leges, kelder, aanschafbudget en tuin.";

export const routeUitkomsten: Record<Projectroute, {
  label: string;
  titel: string;
  tekst: string;
  cta: string;
  href: string;
  verder: readonly { label: string; href: string }[];
}> = {
  kavel: {
    label: "Kavelroute",
    titel: "Eerst weten wat hier mag en wat er overblijft.",
    tekst: "Je plan begint bij de plek. Wij lezen de regels, de bereikbaarheid en de maten die de fabriek aankan, en zeggen welke kostenrichting daarbij hoort.",
    cta: "Laat je kavel checken",
    href: "/kavel",
    verder: [
      { label: "Bekijk de bouwbaarheidscheck", href: "/bouwbaarheid" },
      { label: "Vergelijk pakketten en prijzen", href: "/manieren-van-werken" },
    ],
  },
  ontwerp: {
    label: "Ontwerproute",
    titel: "Je ontwerp toetsen zonder het in te leveren.",
    tekst: "De lijnen liggen er. Wij benoemen wat ongewijzigd kan blijven, waar prefab om een keuze vraagt en wat dat doet met constructie en prijs.",
    cta: "Laat je ontwerp toetsen",
    href: "/ontwerp",
    verder: [
      { label: "Bekijk de bouwsystemen", href: "/systemen" },
      { label: "Bekijk gebouwde projecten", href: "/projecten" },
    ],
  },
  bouwrijp: {
    label: "Bouwrijpe route",
    titel: "Van vergunde set naar montage op je kavel.",
    tekst: "Het papierwerk ligt achter je. Nu vallen productieset, fabriek, fundering, transport en montage in één planning, met de rolverdeling vooraf vast.",
    cta: "Vraag een productieplanning aan",
    href: "/bouwrijp",
    verder: [
      { label: "Bekijk de route fase voor fase", href: "/werkwijze" },
      { label: "Bekijk samenwerken als professional", href: "/professionals" },
    ],
  },
};

/**
 * Maakt van de antwoorden het complete kwalificatierecord. Alleen aanroepen als
 * alle zes stappen beantwoord zijn; de router bewaakt dat.
 */
export function maakKwalificatie(
  antwoorden: Antwoorden,
  herkomst: { bron_pagina: string; bron_component: string; gestart_op: string },
): Leadkwalificatie | null {
  const { kavelstatus, ontwerpstatus, projecttype, investeringsniveau, gewenste_start, rol } = antwoorden;
  if (!kavelstatus || !ontwerpstatus || !projecttype || !investeringsniveau || !gewenste_start || !rol) return null;

  return {
    plaats: antwoorden.plaats.slice(0, 160),
    kavelstatus,
    ontwerpstatus,
    projecttype,
    investeringsniveau,
    gewenste_start,
    rol,
    route: leidRouteAf({ kavelstatus, ontwerpstatus }),
    pakketindicatie: leidPakketAf(investeringsniveau),
    ...herkomst,
  };
}

/**
 * Waar de router zijn uitkomst laat staan. Nog geen opslag en geen doorsturen:
 * dat volgt in een later ticket. Dit is alleen de sessie van deze bezoeker,
 * zodat `/start` en de header de gekozen route kunnen tonen.
 */
export const kwalificatieOpslagsleutel = "evilla:leadkwalificatie:v1";
